import { UserRole } from '@atlas/types'
import {
	ArrowLeftRight,
	Boxes,
	CloudDownload,
	Compass,
	FileCog,
	FileSpreadsheet,
	FileText,
	Home,
	List,
	ListChecks,
	type LucideIcon,
	Megaphone,
	MessagesSquare,
	Network,
	Newspaper,
	Radio,
	Scale,
	ScrollText,
	Settings,
	ShieldCheck,
	Sparkles,
	SquareKanban,
	Users,
	Webhook,
	Workflow,
} from 'lucide-react'

// Config-driven navigation modeling the design's §7 module map + the mockup's
// grouped sidebar (WORKSPACE / LIBRARY / GOVERNANCE). `placeholder` modules render
// the shared PlaceholderPage; `minimumRole` controls role-aware visibility.
// Every entry routes somewhere real — no dead links.

/** A live count a nav entry shows beside its label: each is fetched by the sidebar, not stored here. */
export enum NavBadge {
	/** Open error-level problems of the Workfront adapter. */
	WORKFRONT_OPEN_ERRORS = 'workfront_open_errors',
}

export interface NavModule {
	to: string
	label: string
	icon: LucideIcon
	phase: 'PoC' | 'PH' | 'P2'
	minimumRole?: UserRole
	placeholder?: boolean
	/**
	 * Nested entries, rendered indented under the parent when the parent's section
	 * is active. Each child carries its own `minimumRole`, so a parent visible to
	 * one role can still hide children that need a higher one.
	 */
	children?: readonly NavModule[]
	badge?: NavBadge
}

export interface NavGroup {
	label: string
	items: readonly NavModule[]
}

export const NAV_GROUPS: readonly NavGroup[] = [
	{
		label: 'Workspace',
		items: [
			{ to: '/', label: 'Home', icon: Home, phase: 'PoC' },
			{ to: '/explore', label: 'Explore', icon: Compass, phase: 'PoC' },
			{
				to: '/work-orders',
				label: 'Work Orders',
				icon: SquareKanban,
				phase: 'PoC',
				children: [
					{ to: '/work-orders/briefs', label: 'Factory briefs', icon: FileText, phase: 'PoC' },
					{ to: '/work-orders', label: 'All work orders', icon: List, phase: 'PoC' },
				],
			},
		],
	},
	{
		label: 'Library',
		items: [
			{ to: '/assets', label: 'Library', icon: Boxes, phase: 'PoC' },
			{ to: '/campaigns', label: 'Campaigns', icon: Megaphone, phase: 'PoC', minimumRole: UserRole.USER },
			// "Copy" is the industry term for marketing text and avoids reading as an
			// inbox, which "Messages" does in an app that also has comments and
			// notifications. A display name only: the route, service, table and
			// `message.*` events all keep the domain term.
			{ to: '/messages', label: 'Copy', icon: MessagesSquare, phase: 'PH', placeholder: true },
			// No minimum role: reading an article is open to every authenticated role,
			// including VIEWER. The composer routes carry the authoring gate instead.
			{ to: '/articles', label: 'Articles', icon: Newspaper, phase: 'PoC' },
			{ to: '/rules', label: 'Rules', icon: Scale, phase: 'PoC' },
			{ to: '/prompts', label: 'Prompts', icon: Sparkles, phase: 'PoC' },
			{ to: '/prompt-lists', label: 'Prompt lists', icon: List, phase: 'PoC' },
		],
	},
	{
		label: 'Administration',
		items: [
			{
				to: '/admin',
				label: 'Admin',
				icon: Settings,
				phase: 'PoC',
				minimumRole: UserRole.ADMIN,
				placeholder: true,
				// Ordered by how often they are reached: identity first, then the
				// taxonomy it governs, then the two diagnostic tools.
				children: [
					{ to: '/admin/users', label: 'Users', icon: Users, phase: 'PoC', minimumRole: UserRole.SUPER_ADMIN },
					{ to: '/admin/classification', label: 'Taxonomy', icon: Network, phase: 'PoC', minimumRole: UserRole.ADMIN },
					{ to: '/admin/workflows', label: 'Workflows', icon: Workflow, phase: 'PoC', minimumRole: UserRole.ADMIN },
					{
						to: '/admin/workflows/builder',
						label: 'Workflow builder',
						icon: Workflow,
						phase: 'PoC',
						minimumRole: UserRole.ADMIN,
					},
					{
						to: '/admin/checklist-templates',
						label: 'Checklist templates',
						icon: ListChecks,
						phase: 'PoC',
						minimumRole: UserRole.ADMIN,
					},
					{
						to: '/admin/workfront',
						label: 'Workfront',
						icon: ArrowLeftRight,
						phase: 'PoC',
						minimumRole: UserRole.ADMIN,
						badge: NavBadge.WORKFRONT_OPEN_ERRORS,
					},
					{
						to: '/admin/veeva-vault',
						label: 'Veeva Vault import',
						icon: CloudDownload,
						phase: 'PoC',
						minimumRole: UserRole.ADMIN,
					},
					{
						to: '/admin/asset-sheet',
						label: 'Asset sheet sync',
						icon: FileSpreadsheet,
						phase: 'PoC',
						minimumRole: UserRole.ADMIN,
					},
					{
						to: '/admin/classification/facet-explorer',
						label: 'Facet Explorer',
						icon: Compass,
						phase: 'PoC',
						minimumRole: UserRole.SUPER_ADMIN,
					},
					{
						to: '/admin/classification/context-validator',
						label: 'Context Validator',
						icon: ShieldCheck,
						phase: 'PoC',
						minimumRole: UserRole.SUPER_ADMIN,
					},
					{
						to: '/admin/realtime',
						label: 'Realtime',
						icon: Radio,
						phase: 'PoC',
						minimumRole: UserRole.SUPER_ADMIN,
					},
					{
						to: '/admin/file-processing',
						label: 'File processing',
						icon: FileCog,
						phase: 'PoC',
						minimumRole: UserRole.SUPER_ADMIN,
					},
					{
						to: '/admin/webhooks',
						label: 'Webhooks',
						icon: Webhook,
						phase: 'PoC',
						minimumRole: UserRole.SUPER_ADMIN,
					},
					// ADMIN, unlike the three diagnostics entries above it: reading the
					// audit trail is a governance function, not deployment internals.
					{
						to: '/admin/audit',
						label: 'Audit log',
						icon: ScrollText,
						phase: 'PoC',
						minimumRole: UserRole.ADMIN,
					},
				],
			},
		],
	},
]

/**
 * Flat list of every entry, parents and children alike — used to resolve a label
 * by path (breadcrumb, placeholder page), which must work for nested routes too.
 */
export const NAV_MODULES: readonly NavModule[] = NAV_GROUPS.flatMap((group) =>
	group.items.flatMap((item) => [item, ...(item.children ?? [])]),
)

/** One step in a breadcrumb: a label, and a path when the step is navigable. */
export interface BreadcrumbStep {
	label: string
	/** Absent for the current page, which is rendered as plain text. */
	to?: string
}

/**
 * Resolves the breadcrumb trail for a path, from the nav tree rather than from
 * the URL's segments.
 *
 * Deriving it from the tree is what lets `/admin/users` read `Admin / Users`:
 * the parent's label comes from its nav entry, not from title-casing a slug. It
 * also means a nav restructure moves the breadcrumbs with it.
 *
 * Detail routes (`/assets/:id`, `/rules/:id`) match no nav entry exactly, so the
 * closest ancestor is used — a rule detail page reads `Rules`, which is true and
 * navigable, rather than falling back to `Home`, which is neither.
 *
 * @param {string} pathname - Current location pathname.
 * @returns {BreadcrumbStep[]} Trail from the outermost section to the current page.
 */
export function breadcrumbFor(pathname: string): BreadcrumbStep[] {
	// A child sharing its parent's path (All work orders) names that page, so the last match wins.
	const exact = [...NAV_MODULES].reverse().find((module) => module.to === pathname)
	// Longest prefix wins, so `/admin/classification/facet-explorer` resolves to the
	// facet explorer rather than to `/admin`.
	const closest =
		exact ??
		[...NAV_MODULES]
			.filter((module) => module.to !== '/' && pathname.startsWith(`${module.to}/`))
			.sort((a, b) => b.to.length - a.to.length)[0]

	if (!closest || closest.to === '/') return []

	const parent = NAV_GROUPS.flatMap((group) => group.items).find(
		(item) => item.to !== closest.to && item.children?.some((child) => child.to === closest.to),
	)

	const trail: BreadcrumbStep[] = parent ? [{ label: parent.label, to: parent.to }] : []
	// A detail page keeps its section navigable; the section's own page is the leaf.
	trail.push(exact ? { label: closest.label } : { label: closest.label, to: closest.to })
	return trail
}
